import {
  MutateVariablePayload,
  VariableDto,
  VariableSourceSettingsDto,
} from '@thingslog/repositories';
import { dashboardVariablesQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@thingslog/queries';

interface UseDashboardVariableOperationsResult {
  dashboardVariablesLocal: VariableDto[];
  handleTempSaveVariable: (id: string, payload: MutateVariablePayload) => void;
  handlePersistVariable: (
    id: string,
    payload: MutateVariablePayload,
    callbacks?: DashboardVariableMutationCallbacks
  ) => void;
  handlePersistNewVariable: (
    payload: MutateVariablePayload,
    callbacks?: DashboardVariableMutationCallbacks
  ) => void;
  handleDeleteVariable: (
    variable: VariableDto,
    callbacks?: DashboardVariableMutationCallbacks
  ) => void;
  isPersistingVariable: boolean;
  isPersistingNewVariable: boolean;
  isDeletingVariable: boolean;
}

interface DashboardVariableMutationCallbacks {
  onSuccess?: () => void;
}

export const useDashboardVariableOperations = (
  dashboardId: string,
  companyId: string | number | null,
  variableSources: VariableSourceSettingsDto[]
): UseDashboardVariableOperationsResult => {
  const {
    useDashboardVariables: useDashboardVariablesQuery,
    useCreateDashboardVariable,
    useDeleteDashboardVariable,
    useUpdateDashboardVariable,
  } = dashboardVariablesQueryClient;
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<VariableDto[] | null>(null);
  const [tempSave, setTempSave] = useState<VariableDto[]>([]);

  const dashboardVariablesQuery = useDashboardVariablesQuery(dashboardId, companyId, {
    enabled: !!dashboardId,
    refetchOnWindowFocus: false,
    onSuccess: (data: VariableDto[]): void => {
      setSnapshot(data);
    },
  });

  useEffect(() => {
    setSnapshot(null);
    setTempSave([]);
  }, [dashboardId]);

  useEffect(() => {
    if (dashboardVariablesQuery.data) {
      setSnapshot(dashboardVariablesQuery.data);
    }
  }, [dashboardVariablesQuery.data]);

  const convertPayloadToVariable = useCallback(
    (
      id: string,
      payload: MutateVariablePayload,
      sources: VariableSourceSettingsDto[]
    ): VariableDto => {
      const variableSource = sources.find(
        (source: VariableSourceSettingsDto) => source.id === payload.variableSourceId
      );

      return {
        id: id,
        label: payload.label,
        type: payload.type,
        name: payload.name,
        variableSource: variableSource ?? null,
        value: payload.value,
      } as VariableDto;
    },
    []
  );

  const dashboardVariablesSnapshot = useMemo(() => {
    return snapshot ?? [];
  }, [snapshot]);

  const handleTempSaveVariable = useCallback(
    (id: string, payload: MutateVariablePayload): void => {
      const variable = convertPayloadToVariable(id, payload, variableSources);

      setTempSave((prev: VariableDto[]): VariableDto[] => {
        if (prev.find((v: VariableDto) => v.id === id)) {
          return prev.map((v: VariableDto): VariableDto => {
            if (v.id === id) {
              return variable;
            }
            return v;
          });
        }
        return [...prev, variable];
      });
    },
    [convertPayloadToVariable, variableSources]
  );

  const applyPersistedVariable = useCallback((variable: VariableDto): void => {
    setSnapshot((prev: VariableDto[] | null): VariableDto[] => {
      const current = prev ?? [];
      const exists = current.some((item: VariableDto): boolean => item.id === variable.id);

      if (!exists) {
        return [...current, variable];
      }

      return current.map((item: VariableDto): VariableDto => {
        if (item.id === variable.id) {
          return variable;
        }

        return item;
      });
    });

    setTempSave((prev: VariableDto[]): VariableDto[] => {
      return prev.filter((item: VariableDto): boolean => item.id !== variable.id);
    });
  }, []);

  const removeVariableLocally = useCallback((variableId: string): void => {
    setSnapshot((prev: VariableDto[] | null): VariableDto[] => {
      return (prev ?? []).filter((item: VariableDto): boolean => item.id !== variableId);
    });

    setTempSave((prev: VariableDto[]): VariableDto[] => {
      return prev.filter((item: VariableDto): boolean => item.id !== variableId);
    });
  }, []);

  const createVariableMutation = useCreateDashboardVariable({
    onSuccess: (
      variable: VariableDto,
      variables: {
        dashboardId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }
    ): void => {
      applyPersistedVariable(variable);
      queryClient.invalidateQueries([
        QueryKeys.GetVariables,
        variables.dashboardId,
        variables.companyId,
      ]);
    },
  });

  const updateVariableMutation = useUpdateDashboardVariable({
    onSuccess: (
      variable: VariableDto,
      variables: {
        dashboardId: string;
        variableId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }
    ): void => {
      applyPersistedVariable(variable);
      queryClient.invalidateQueries([
        QueryKeys.GetVariables,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetVariable,
        variables.dashboardId,
        variables.variableId,
        variables.companyId,
      ]);
    },
  });

  const deleteVariableMutation = useDeleteDashboardVariable({
    onSuccess: (
      _deleted: boolean,
      variables: { dashboardId: string; variableId: string; companyId?: string | number | null }
    ): void => {
      removeVariableLocally(variables.variableId);
      queryClient.invalidateQueries([
        QueryKeys.GetVariables,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetVariable,
        variables.dashboardId,
        variables.variableId,
        variables.companyId,
      ]);
    },
  });

  const handlePersistVariable = useCallback(
    (
      variableId: string,
      payload: MutateVariablePayload,
      callbacks?: DashboardVariableMutationCallbacks
    ): void => {
      if (!dashboardId) {
        return;
      }

      updateVariableMutation.mutate(
        {
          dashboardId,
          variableId,
          body: payload,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [dashboardId, companyId, updateVariableMutation]
  );

  const handlePersistNewVariable = useCallback(
    (payload: MutateVariablePayload, callbacks?: DashboardVariableMutationCallbacks): void => {
      if (!dashboardId) {
        return;
      }

      createVariableMutation.mutate(
        {
          dashboardId,
          body: payload,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [dashboardId, companyId, createVariableMutation]
  );

  const handleDeleteVariable = useCallback(
    (variable: VariableDto, callbacks?: DashboardVariableMutationCallbacks): void => {
      if (!dashboardId) {
        return;
      }

      deleteVariableMutation.mutate(
        {
          dashboardId,
          variableId: variable.id,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [dashboardId, companyId, deleteVariableMutation]
  );

  const localVariables = useMemo(() => {
    return dashboardVariablesSnapshot.map((v: VariableDto): VariableDto => {
      const temp = tempSave.find((t: VariableDto) => t.id === v.id);
      if (temp) {
        return temp;
      }
      return v;
    });
  }, [dashboardVariablesSnapshot, tempSave]);

  return {
    dashboardVariablesLocal: localVariables,
    handleTempSaveVariable,
    handlePersistVariable,
    handlePersistNewVariable,
    handleDeleteVariable,
    isPersistingVariable: updateVariableMutation.isLoading,
    isPersistingNewVariable: createVariableMutation.isLoading,
    isDeletingVariable: deleteVariableMutation.isLoading,
  };
};
